/*
  A partner's own view of a customer's Asset Management table.

  A partner reading a customer's profile is reading the customer's data: their
  assets, and the columns the CUSTOMER chose to present
  (`asset_management_presented_fields`, saved on their account). How a partner
  wants to look at that is not a change to the customer's account, so it is
  never written there. It lives here instead.

  sessionStorage, not localStorage, on purpose. A view a partner set up while
  reading one customer should not still be waiting for them next week, and it
  should not follow them onto a shared machine's next session. This survives a
  refresh -- which is the whole point of storing it at all -- and ends when the
  tab does.

  Keyed by customer: two customers opened in one session keep their own views,
  and a partner who has never touched the menu for a customer gets that
  customer's own columns, as before.

  Nothing in here reaches a server, and nothing in here is a secret: it is a
  list of column names, a search box's contents and a sort. The customer's data
  stays where it was.
*/

const PREFIX = "ostreon.asset_table_view.";

/*
  Every access is wrapped. `sessionStorage` is not merely empty but THROWS when
  a browser blocks site data -- a private window, an embedded webview, a
  hardened profile -- and a partner who cannot persist a view should still be
  able to set one.
*/
export const readTableView = (customerId) => {
  if (!customerId) return null;
  let view = null;
  try {
    const raw = window.sessionStorage.getItem(PREFIX + customerId);
    view = raw ? JSON.parse(raw) : null;
  } catch {
    // Unreadable or not JSON: start from the customer's own view.
    return null;
  }
  if (!view || typeof view !== "object" || Array.isArray(view)) return null;
  /*
    Shaped on the way out, not trusted as found. This is the one value in the
    table that a reader can edit by hand, and a string where a list belongs
    would take the page down on the first filter. Nothing here is a
    permission, so there is nothing to guard beyond the shape.
  */
  const names = (value) =>
    Array.isArray(value) ? value.filter((v) => typeof v === "string") : null;
  const sort = view.sort;
  return {
    // null is "not chosen", and falls back to the customer's own columns.
    fields: names(view.fields),
    search: typeof view.search === "string" ? view.search : "",
    assetTypes: names(view.assetTypes) || [],
    exposures: names(view.exposures) || [],
    sort:
      sort && typeof sort === "object" && typeof sort.key === "string"
        ? { key: sort.key, desc: Boolean(sort.desc) }
        : { key: null, desc: false },
  };
};

export const writeTableView = (customerId, view) => {
  if (!customerId) return;
  try {
    window.sessionStorage.setItem(PREFIX + customerId, JSON.stringify(view));
  } catch {
    // No storage. The view still works; it just will not survive a refresh.
  }
};
